import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button } from '@/components/atoms';
import { ProductSearch, SalesCart, SalesCheckout } from '@/components/organisms/Sales';
import type { CartItem, SaleCheckoutData } from '@/components/organisms/Sales';
import { useCreateSaleMutation } from '@/hooks/useErpApi';
import type { PricedProduct } from '@/hooks/usePricedProducts';

interface SaleTab {
  id: string;
  title: string;
  isActive: boolean;
  cart: CartItem[];
  warehouseId: string;
}

export default function CreateSalesPage() {
  const navigate = useNavigate();
  const { mutate: createSale } = useCreateSaleMutation();

  const [tabs, setTabs] = useState<SaleTab[]>([
    { id: 'sale-1', title: 'Sale #1', isActive: true, cart: [], warehouseId: '' },
  ]);
  const [activeTabId, setActiveTabId] = useState('sale-1');
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleAddTab = useCallback(() => {
    const newTabNumber = tabs.length + 1;
    const newTab: SaleTab = {
      id: `sale-${Date.now()}`,
      title: `Sale #${newTabNumber}`,
      isActive: true,
      cart: [],
      warehouseId: '',
    };

    setTabs((prev) => prev.map((tab) => ({ ...tab, isActive: false })).concat(newTab));
    setActiveTabId(newTab.id);
  }, [tabs.length]);

  // Listen for add-tab event from navbar
  useEffect(() => {
    const handleAddTabEvent = () => {
      handleAddTab();
    };

    window.addEventListener('add-sales-tab', handleAddTabEvent);
    return () => window.removeEventListener('add-sales-tab', handleAddTabEvent);
  }, [handleAddTab]);

  const handleCloseTab = (tabId: string) => {
    if (tabs.length === 1) {
      navigate('/sales');
      return;
    }

    const tabIndex = tabs.findIndex((tab) => tab.id === tabId);
    const newTabs = tabs.filter((tab) => tab.id !== tabId);

    setTabs(newTabs);

    if (tabId === activeTabId) {
      const newActiveTab = newTabs[tabIndex] || newTabs[tabIndex - 1];
      setActiveTabId(newActiveTab.id);
    }
  };

  const handleSwitchTab = (tabId: string) => {
    setActiveTabId(tabId);
  };

  const activeTab = tabs.find((tab) => tab.id === activeTabId);

  // Price item type from API response
  interface PriceItem {
    price_type?: string;
    price: number;
    is_active?: boolean;
  }

  // Helper function to extract prices from the prices array
  const extractPrices = (prices: PriceItem[] | undefined) => {
    let retailPrice: number | undefined;
    let memberPrice: number | undefined;
    let mspPrice: number | undefined;
    let mrpPrice: number | undefined;

    if (prices && Array.isArray(prices)) {
      for (const p of prices) {
        if (!p.is_active) continue;
        const priceType = p.price_type?.toLowerCase();

        switch (priceType) {
          case 'retail':
            retailPrice = p.price;
            break;
          case 'member':
            memberPrice = p.price;
            break;
          case 'msp':
            mspPrice = p.price;
            break;
          case 'mrp':
            mrpPrice = p.price;
            break;
          // Legacy support for SALE/selling_price
          case 'sale':
          case 'selling_price':
            if (!retailPrice) retailPrice = p.price;
            break;
        }
      }
    }

    return { retailPrice, memberPrice, mspPrice, mrpPrice };
  };

  // Cart operations for active tab
  const handleAddToCart = (product: PricedProduct) => {
    if (!activeTab) return;

    // Extract prices from the prices array
    const pricesArray = product.prices;
    const { retailPrice, memberPrice, mspPrice, mrpPrice } = extractPrices(pricesArray);

    // Determine the selling price with fallback chain:
    // 1. retail price (primary selling price)
    // 2. MSP (maximum selling price)
    // 3. MRP (maximum retail price)
    // 4. Legacy selling_price from usePricedProducts
    // 5. cost_price as last resort
    const itemPrice =
      retailPrice || mspPrice || mrpPrice || product.selling_price || product.cost_price || 0;

    setTabs((prev) =>
      prev.map((tab) => {
        if (tab.id !== activeTabId) return tab;

        // Check if product already in cart
        const existingItemIndex = tab.cart.findIndex(
          (item) => item.variantId === product.variant_id
        );

        if (existingItemIndex >= 0) {
          // Update quantity
          const updatedCart = [...tab.cart];
          updatedCart[existingItemIndex] = {
            ...updatedCart[existingItemIndex],
            quantity: updatedCart[existingItemIndex].quantity + 1,
          };
          return { ...tab, cart: updatedCart };
        } else {
          // Add new item with all price types and GST rates
          const newItem: CartItem = {
            variantId: product.variant_id,
            productName: product.product_name,
            productSku: product.sku || '',
            quantity: 1,
            price: itemPrice, // Default to retail price
            retailPrice: itemPrice, // Store retail price separately
            memberPrice: memberPrice, // Store member price if available
            mspPrice: mspPrice, // Store MSP if available
            availableStock: product.total_quantity,
            gst_rate: (product as any).gst_rate,
            cgst_rate: (product as any).cgst_rate,
            sgst_rate: (product as any).sgst_rate,
          };
          return { ...tab, cart: [...tab.cart, newItem], warehouseId: product.warehouse_id || '' };
        }
      })
    );

    // Show warning if no retail price was found
    if (!retailPrice && !mspPrice && !mrpPrice && !product.selling_price) {
      setError(
        `Warning: No selling price set for ${product.product_name}. Using cost price (₹${itemPrice}) as fallback.`
      );
      setTimeout(() => setError(null), 5000);
    } else {
      const priceInfo = memberPrice ? ` (Member: ₹${memberPrice})` : '';
      setSuccess(`Added ${product.product_name} to cart${priceInfo}`);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleUpdateQuantity = (variantId: string, quantity: number) => {
    if (!activeTab) return;

    setTabs((prev) =>
      prev.map((tab) => {
        if (tab.id !== activeTabId) return tab;

        const updatedCart = tab.cart.map((item) =>
          item.variantId === variantId ? { ...item, quantity } : item
        );
        return { ...tab, cart: updatedCart };
      })
    );
  };

  const handleRemoveItem = (variantId: string) => {
    if (!activeTab) return;

    setTabs((prev) =>
      prev.map((tab) => {
        if (tab.id !== activeTabId) return tab;

        const updatedCart = tab.cart.filter((item) => item.variantId !== variantId);
        return { ...tab, cart: updatedCart };
      })
    );
  };

  const handleClearCart = () => {
    if (!activeTab) return;

    if (window.confirm('Are you sure you want to clear the cart?')) {
      setTabs((prev) =>
        prev.map((tab) => {
          if (tab.id !== activeTabId) return tab;
          return { ...tab, cart: [] };
        })
      );
    }
  };

  const handleCheckout = () => {
    setIsCheckoutOpen(true);
  };

  const handleConfirmSale = async (checkoutData: SaleCheckoutData) => {
    setError(null);

    try {
      await createSale(checkoutData);
      setSuccess('Sale created successfully!');

      // Close tab after successful sale
      setTimeout(() => {
        handleCloseTab(activeTabId);
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create sale');
      throw err;
    }
  };

  if (!activeTab) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/sales')}
            className="p-2 rounded-lg hover:bg-neutral-100 transition-colors"
          >
            <Icon name="ChevronLeft" size={24} className="text-neutral-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">Sales POS</h1>
            <p className="text-neutral-600 mt-1">Fast, modern point-of-sale interface</p>
          </div>
        </div>
        <Button variant="primary" onClick={handleAddTab}>
          <Icon name="Plus" size={20} className="mr-2" />
          New Sale Tab
        </Button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-success-50 border border-success-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="Check" size={20} className="text-success-600" />
            <p className="text-success-700">{success}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="CircleAlert" size={20} className="text-error-600" />
            <p className="text-error-700">{error}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
        <div className="flex items-center gap-1 px-4 pt-4 border-b border-neutral-200 overflow-x-auto">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className={`flex items-center gap-2 px-4 py-3 rounded-t-lg cursor-pointer transition-all ${
                tab.id === activeTabId
                  ? 'bg-white border-t border-l border-r border-neutral-200 -mb-px'
                  : 'bg-neutral-50 hover:bg-neutral-100'
              }`}
              onClick={() => handleSwitchTab(tab.id)}
            >
              <span
                className={`text-sm font-medium ${
                  tab.id === activeTabId ? 'text-primary-600' : 'text-neutral-600'
                }`}
              >
                {tab.title}
              </span>
              {tab.cart.length > 0 && (
                <span className="bg-primary-100 text-primary-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {tab.cart.length}
                </span>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseTab(tab.id);
                }}
                className="p-1 rounded hover:bg-neutral-200 transition-colors"
              >
                <Icon name="X" size={16} className="text-neutral-500" />
              </button>
            </div>
          ))}
        </div>

        {/* POS Interface - Split Screen */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6 min-h-[600px]">
          {/* Left: Product Search (2 columns) */}
          <div className="lg:col-span-2">
            <ProductSearch warehouseId={activeTab.warehouseId} onProductSelect={handleAddToCart} />
          </div>

          {/* Right: Cart (1 column) */}
          <div className="lg:col-span-1">
            <SalesCart
              items={activeTab.cart}
              onUpdateQuantity={handleUpdateQuantity}
              onRemoveItem={handleRemoveItem}
              onClearCart={handleClearCart}
              onCheckout={handleCheckout}
              warehouseId={activeTab.warehouseId}
            />
          </div>
        </div>
      </div>

      {/* Performance Tips */}
      <div className="bg-gradient-to-br from-info-50 to-info-100 border border-info-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Zap" size={20} className="text-info-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-info-900 mb-1">POS Tips for Fast Sales</h4>
            <ul className="text-sm text-info-700 space-y-1">
              <li>• Use Quick Access tiles for frequently sold products</li>
              <li>• Search by product name or SKU for instant results</li>
              <li>• Multiple tabs allow handling concurrent customers</li>
              <li>• Real-time inventory prevents overselling</li>
              <li>• FEFO ensures oldest batches are sold first</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Checkout Modal */}
      <SalesCheckout
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        cartItems={activeTab.cart}
        defaultWarehouseId={activeTab.warehouseId}
        onConfirmSale={handleConfirmSale}
      />
    </div>
  );
}
